import { ChevronRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import type { AudienceKind } from './AudienceCard'

/**
 * SearchResult — one row in the docs site /search results list.
 *
 * Design plan reference,
 * section 22.5 (search results page) + section 27.1 (SearchResult
 * type contract). Pairs with the Pagefind-powered SearchPalette
 * (Track D / shipped via CommandPalette REUSE).
 *
 * Shape per row:
 *
 *   ┌─ AUDIENCE ────────────────────────────┐  ┌────────────┐
 *   │ Docs › Audience › Section › Topic     │  │  5 min      │  (optional)
 *   │ Title of the topic                    │  └────────────┘
 *   │ Snippet with <mark>highlighted</mark> │
 *   │ terms surfaced by Pagefind.           │
 *   └────────────────────────────────────────┘
 *
 * The audience badge uses the same {bg-muted, text-text} token pair
 * family as AudienceCard + ArticleHeader so the audience signal is
 * visually consistent across surfaces.
 *
 * The snippet_html is whatever Pagefind emits, which includes the
 * `<mark>` highlight tags. The component renders it via
 * dangerouslySetInnerHTML — Pagefind's output is content-sourced
 * from the same Markdoc tree the docs site already built, so the
 * surface area is closed (no untrusted input path).
 *
 * Per plan section 28.1, the reading-time chip surfaces ONLY when
 * the topic's content_type is one where "how long this will take"
 * is actually useful — guides and tutorials. References and
 * troubleshooting pages skip it.
 */

export type SearchResultContentType =
  | 'guide'
  | 'concept'
  | 'tutorial'
  | 'reference'
  | 'manual'
  | 'troubleshooting'

export interface SearchResultProps extends React.HTMLAttributes<HTMLElement> {
  audience: AudienceKind
  contentType: SearchResultContentType
  /** Path segments e.g. ['Docs', 'Tenant admin', 'MFA', 'Configure']. */
  breadcrumb: string[]
  title: string
  /**
   * Pagefind-emitted excerpt — already contains `<mark>` tags around
   * the matched terms. Rendered as innerHTML.
   */
  snippetHtml: string
  /** Topic URL. */
  docsPath: string
  /** Minutes-to-read. Renders only when contentType is 'guide' | 'tutorial'. */
  estimatedRead?: number
}

const AUDIENCE_LABEL: Record<AudienceKind, string> = {
  developer: 'Developer',
  tenant_admin: 'Tenant admin',
  cp_admin: 'Control plane admin',
}

const AUDIENCE_BADGE_CLASS: Record<AudienceKind, string> = {
  developer: 'bg-ia-cyan-muted text-ia-cyan-text',
  tenant_admin: 'bg-ia-green-muted text-ia-green-text',
  cp_admin: 'bg-ia-indigo-muted text-ia-indigo-text',
}

/** Content types where reading time is meaningful enough to render. */
const SHOW_READ_TIME_FOR: SearchResultContentType[] = ['guide', 'tutorial']

export const SearchResult = React.forwardRef<HTMLElement, SearchResultProps>(
  (
    {
      audience,
      contentType,
      breadcrumb,
      title,
      snippetHtml,
      docsPath,
      estimatedRead,
      className,
      ...rest
    },
    ref,
  ) => {
    const showReadTime =
      typeof estimatedRead === 'number' &&
      SHOW_READ_TIME_FOR.includes(contentType)

    return (
      <article
        ref={ref}
        className={cn(
          'group rounded-lg border border-ia-border bg-card p-4',
          'transition-[border-color] duration-100 motion-reduce:transition-none',
          'hover:border-[var(--color-ia-sidebar-active-border)]',
          'focus-within:border-[var(--color-ia-sidebar-active-border)]',
          className,
        )}
        {...rest}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <span
            className={cn(
              'inline-flex items-center rounded-pill px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
              AUDIENCE_BADGE_CLASS[audience],
            )}
          >
            {AUDIENCE_LABEL[audience]}
          </span>
          {showReadTime && (
            <span className="text-caption text-ia-text-secondary whitespace-nowrap">
              {estimatedRead} min
            </span>
          )}
        </div>

        <nav
          aria-label="Result breadcrumb"
          className="flex items-center flex-wrap gap-1 mb-1 text-caption text-ia-text-secondary"
        >
          {breadcrumb.map((segment, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <ChevronRight
                  size={10}
                  className="text-ia-text-tertiary shrink-0"
                  aria-hidden="true"
                />
              )}
              <span>{segment}</span>
            </React.Fragment>
          ))}
        </nav>

        <h3 className="m-0 text-base font-semibold text-ia-text mb-1">
          <a
            href={docsPath}
            className="hover:text-ia-green-text focus-visible:outline-none focus-visible:text-ia-green-text"
          >
            {title}
          </a>
        </h3>

        <p
          className="m-0 text-body-sm text-ia-text-secondary [&_mark]:bg-ia-amber-muted [&_mark]:text-ia-text [&_mark]:rounded-sm [&_mark]:px-0.5"
          dangerouslySetInnerHTML={{ __html: snippetHtml }}
        />
      </article>
    )
  },
)
SearchResult.displayName = 'SearchResult'
